import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Layers, MoreHorizontal } from 'lucide-react';
import { funnelExpressionsAPI, funnelsAPI } from 'services/swagger-api';
import { GenericLoading } from 'components/ui/Loader';
import { EmptyState } from 'components/ui/EmptyState';
import { FunnelDetailProvider, isParkableFunnel } from './FunnelDetailContext';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { toExpressionVariables } from './funnelVariables';
import { DetailBlock } from './blocks/DetailBlock';
import { GuaranteesBlock } from './blocks/GuaranteesBlock';
import { FeesBlock } from './blocks/FeesBlock';
import { ParkBlock } from './blocks/ParkBlock';
import { VariablesBlock } from './blocks/VariablesBlock';
import { SupportTypesBlock } from './blocks/SupportTypesBlock';
import { PricingBlock } from './blocks/PricingBlock';
import { ValidationBlock } from './blocks/ValidationBlock';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { TunnelActionsMenu } from '../TunnelActionsMenu';
import { tunnelTypeLabel } from './funnelEnums';

/**
 * Page de gestion d'un tunnel.
 *
 * Deux colonnes appariées : à gauche ce qui se règle, à droite ce qui en découle
 * directement. Les garanties font face à la tarification qu'elles produisent, les frais
 * font face aux variables que leurs formules consomment. La liste des garanties occupait
 * auparavant toute la largeur pour trois colonnes de contenu, ce qui déséquilibrait la
 * page et repoussait la tarification en bas d'écran.
 */
export const FunnelDetails: FC = () => {
  const { tunnelId } = useParams<{ tunnelId: string }>();
  const navigate = useNavigate();

  const fetchFunnel = useCallback(
    () => funnelsAPI.getFunnelDetail({ funnelId: tunnelId as string }),
    [tunnelId]
  );
  const funnelResource = useAsyncResource(fetchFunnel, Boolean(tunnelId));

  const fetchVariables = useCallback(
    () => funnelExpressionsAPI.getVariablesByContext({ contextObjectId: tunnelId as string }),
    [tunnelId]
  );
  const variablesResource = useAsyncResource(fetchVariables, Boolean(tunnelId));

  const { createMenuItem } = useActionMenu();

  // Incrémenté par les blocs qui modifient garanties, frais ou config parc.
  const [pricingVersion, setPricingVersion] = useState(0);
  const invalidatePricing = useCallback(() => setPricingVersion((v) => v + 1), []);

  const variables = useMemo(() => variablesResource.data ?? [], [variablesResource.data]);
  const expressionVariables = useMemo(() => toExpressionVariables(variables), [variables]);

  const funnel = funnelResource.data;

  if (funnelResource.isLoading) {
    return (
      <section className="w-full rounded-2xl border bg-white p-8">
        <GenericLoading />
      </section>
    );
  }

  if (funnelResource.error || !funnel || !tunnelId) {
    return (
      <section className="w-full rounded-2xl border bg-white">
        <EmptyState
          icon={<Layers className="h-8 w-8 text-slate-400" />}
          title={funnelResource.error ?? 'Tunnel introuvable'}
          description="Le tunnel demandé n'a pas pu être chargé."
        />
      </section>
    );
  }

  return (
    <FunnelDetailProvider
      value={{
        tunnelId,
        funnel,
        variables,
        expressionVariables,
        reloadFunnel: funnelResource.reload,
        reloadVariables: variablesResource.reload,
        pricingVersion,
        invalidatePricing,
      }}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/back-office/tunnels')}
            className="inline-flex items-center gap-2 rounded-lg px-2 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary"
          >
            <ArrowLeft size={16} />
            Retour à la liste des tunnels
          </button>

          {/* Duplication, historique et suppression sous le même « ⋯ » : aucune n'est une
              action de lecture courante, et l'en-tête reste lisible. Le tunnel supprimé
              n'a plus de fiche, on repart alors sur la liste. */}
          <TunnelActionsMenu
            tunnel={{
              id: tunnelId,
              code: funnel.code,
              label: funnel.designation,
              tunnelType: funnel.tunnelType
            }}
            onDeleted={() => navigate('/back-office/tunnels')}
            extraItems={[
              createMenuItem(
                'audit-history',
                "Historique d'audit",
                () => navigate(`/back-office/tunnels/${tunnelId}/audit-history`),
                { icon: 'history' }
              )
            ]}
            trigger={
              <span
                role="button"
                tabIndex={0}
                aria-label="Actions du tunnel"
                className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary/90"
              >
                <MoreHorizontal size={16} />
                Actions
                <ChevronDown size={14} className="opacity-80" />
              </span>
            }
          />
        </div>

        {/* Rappel permanent de ce qu'on est en train de modifier : la page est longue et
            le nom du tunnel n'apparaissait qu'à l'intérieur du bloc Détail. */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-2xl font-bold text-zinc-800">
            {funnel.designation || 'Tunnel sans désignation'}
          </h1>
          {funnel.produit?.libelle && (
            <span className="text-lg text-gray-500">({funnel.produit.libelle})</span>
          )}
          {funnel.code && (
            <span className="rounded bg-white px-2 py-0.5 font-mono text-xs text-gray-500 shadow-sm">
              {funnel.code}
            </span>
          )}
          {/* Le type conditionne les supports acceptés et le parcours de souscription :
              il a sa place à côté du statut, pas seulement dans la liste. Une valeur hors
              énumération n'affiche rien plutôt qu'un type inventé. */}
          {tunnelTypeLabel(funnel.tunnelType) && (
            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
              {tunnelTypeLabel(funnel.tunnelType)}
            </span>
          )}
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              funnel.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
            }`}
          >
            {funnel.isActive ? 'Actif' : 'Inactif'}
          </span>
        </div>

        <DetailBlock />

        {/* Who validates the quotes of this tunnel, and for which brokers. */}
        <ValidationBlock />

        {/* Les garanties et le prix qu'elles produisent. */}
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
          <GuaranteesBlock />
          <PricingBlock />
        </div>

        {/* Les frais et les variables que leurs formules consomment. La configuration
            de parc suit immédiatement les frais : elle ne fait que préciser comment
            chacun d'eux se comporte sur un contrat multi-biens. */}
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
          <div className="flex flex-col gap-5">
            <FeesBlock />
            {/* Le back refuse la configuration de parc hors branches GLI / CBNO. */}
            {isParkableFunnel(funnel) && <ParkBlock />}
          </div>

          <div className="flex flex-col gap-5">
            <VariablesBlock />
            <SupportTypesBlock />
          </div>
        </div>
      </div>
    </FunnelDetailProvider>
  );
};

export default FunnelDetails;
